import * as React from 'react';
import { useEffect, useState } from "react";
import { apiConstants, postGeneric, post } from "../../common/constants";
import { useSnackbar } from "notistack";
import _ from "lodash";
import { Button, Typography, Card, CardContent, Grid, Box, Chip, Tab, FormControlLabel, Switch } from '@mui/material';
import { TabContext, TabList, TabPanel } from '@mui/lab';
import { useForm } from 'react-hook-form';
import StrandedResiduals from '../Wallet/StrandedResiduals';
import WalletSwitchHistory from '../Wallet/WalletSwitchHistory';
import { WALLET_TYPE_OPTIONS } from '../Wallet/walletUi';
import ProzoDataGrid from '../../common/ProzoDataGrid';
import { FormProvider, RHFSearchableSelectField } from '../../common/hook-form';
import PageTitle from '../../components/PageTitle';
import Loader from '../../common/Loader';
import { formatDate } from "../../common/util";
import { formatCurrency } from '../../helpers/UtilityHelper';
// Import necessary utilities for CSV export
import { convertToCSV, downloadCSV } from '../../helpers/UtilityHelper';
// Import the DownloadConfirmationModal component
import DownloadConfirmationModal from '../../components/DownloadConfirmationModal';

export default function MerchantWallet() {
    const methods = useForm({
        defaultValues: {
            merchant: ''
        }
    });
    const { reset } = methods;

    const [loading, setLoading] = useState(false);
    const [data, setData] = useState([]);
    const [page, setPage] = useState(0);
    const [sizePerPage, setSizePerPage] = useState(20);
    const [filter, setFilter] = useState();
    const [merchantDropdownData, setMerchantDropdownData] = useState([]);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const { enqueueSnackbar } = useSnackbar();
    const [walletTypeFilter, setWalletTypeFilter] = useState('');

    // Add state to manage modal visibility
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [tab, setTab] = useState('1');
    // when on, the grid also shows deactivated wallets so a merchant's two wallets
    // (e.g. old Prepaid + current Postpaid) can be seen side by side
    const [includeInactive, setIncludeInactive] = useState(false);

    // Remove the watch for the merchant field
    // const selectedMerchant = watch('merchant');

    // Remove the useEffect that was watching selectedMerchant
    // useEffect(() => {
    //     setFilter(selectedMerchant);
    // }, [selectedMerchant]);

    const columns = [
        {
            field: 'merchantName',
            headerName: 'Merchant Name',
            minWidth: 200,
            flex: 2,
            align: 'left',
            headerAlign: 'left',
        },
        {
            field: 'merchantEmail',
            headerName: 'Email Address',
            minWidth: 250,
            flex: 2,
            align: 'left',
            headerAlign: 'left',
        },
                {
            field: 'walletBalance',
            headerName: 'Prepaid Wallet Balance',
            minWidth: 150,
            flex: 1,
            align: 'right',
            headerAlign: 'center',
            renderCell: (params) => (
                <span style={{ 
                    fontWeight: 600,
                    color: params.row.walletBalance >= 0 ? '#28a745' : '#dc3545'
                }}>
                    {formatCurrency(params.row.walletBalance)}
                </span>
            )
        },
        {
            field: 'creditLimit',
            headerName: 'Credit Limit',
            minWidth: 130,
            flex: 1,
            align: 'right',
            headerAlign: 'right',
            renderCell: (params) => (
                <span style={{ 
                    fontWeight: 500,
                    color: '#6c757d'
                }}>
                    {formatCurrency(params.row.creditLimit || 0)}
                </span>
            )
        },
        {
            field: 'dueAmount',
            headerName: 'Due Amount (Postpaid Merchants)',
            minWidth: 200,
            flex: 1.2,
            align: 'right',
            headerAlign: 'center',
            renderCell: (params) => (
                <span style={{ 
                    fontWeight: 600,
                    color: params.row.dueAmount > 0 ? '#dc3545' : '#6c757d'
                }}>
                    {formatCurrency(params.row?.dueAmount?.toFixed(0) || 0)}
                </span>
            )
        },
        {
            field: 'walletType',
            headerName: 'Type',
            minWidth: 110,
            flex: 0.8,
            align: 'center',
            headerAlign: 'center',
            renderCell: (params) => {
                const type = params.row.walletType;
                return (
                    <Chip
                        label={type}
                        color={type === 'Prepaid' ? 'primary' : 'secondary'}
                        size="small"
                        variant="outlined"
                    />
                );
            }
        },
        {
            field: 'isActive',
            headerName: 'Status',
            minWidth: 100,
            flex: 0.8,
            align: 'center',
            headerAlign: 'center',
            renderCell: (params) => {
                const isActive = params.row.isActive;
                return (
                    <Chip
                        label={isActive ? 'Active' : 'Inactive'}
                        color={isActive ? 'success' : 'error'}
                        size="small"
                        variant="filled"
                    />
                );
            }
        },
        {
            field: 'createdAt',
            headerName: 'Created Date',
            minWidth: 140,
            flex: 1.2,
            align: 'center',
            headerAlign: 'center',
            renderCell: (params) => formatDate(params.row.createdAt)
        }
    ];

    // Fetch merchant dropdown data
    useEffect(() => {
        setIsSubmitting(true);
        post(apiConstants.MERCHANT_SEARCH_CACHED, {
            "offset": 0,
            "limit": 10000,
            "andfilter": [],
            "includes": "name,id,companyName,email,test_merchant"
        })
            .then((res) => {
                const allMerchants = res.data?.data || [];
                setMerchantDropdownData(allMerchants);
            })
            .catch((err) => {
                enqueueSnackbar("Error loading merchants!", { variant: 'error' });
                console.log("API Error:", err);
            })
            .finally(() => setIsSubmitting(false));
    }, []);

    // fetch: runs on load and whenever the inactive toggle flips — kept separate from the
    // client-side filtering below so toggling "Include inactive" re-fetches even while a
    // merchant/type filter is applied (previously the toggle silently did nothing then)
    useEffect(() => {
        setLoading(true);
        const payload = {
            "collection": "merchant_wallet",
            limit: 10000,
            andfilter: includeInactive ? [] : [
                {
                    field: "isActive",
                    operator: "eq",
                    value: true
                }
            ],
        };
        postGeneric(apiConstants.REPORT_GENERIC_SEARCH, payload).then((res) => {
            // drop malformed stub documents (no merchantRef/walletType) — they carry no data
            // and collide row ids with real wallets created in the same second
            const clean = (res?.data?.data || []).filter((w) => w.merchantRef && w.walletType);
            const sortedData = _.sortBy(clean, 'merchantName');
            setData(sortedData);
            setLoading(false);
        }).catch((err) => {
            enqueueSnackbar("Some error occurred!", { variant: 'error' });
            console.log(err);
            setLoading(false);
        });
    }, [includeInactive]);

    // client-side merchant/type filtering — derived at render, no extra state
    const visibleRows = data.filter(wallet =>
        (!filter || wallet.merchantRef === filter) &&
        (!walletTypeFilter || wallet.walletType === walletTypeFilter));

    // Create dropdown options from merchant data
    const merchantOptions = merchantDropdownData.map((merchant) => ({
        value: merchant.id,
        label: merchant.companyName ? `${merchant.name} (${merchant.companyName})` : merchant.name
    }));

    // Calculate summary metrics

    // Remove onChange handlers and implement onSubmit function
    const onSubmit = (data) => {
        const { merchant, walletType } = data;
        setFilter(merchant);
        setWalletTypeFilter(walletType);
    };

    // Define headers for CSV export
    const csvHeaders = [
        { label: 'Merchant Name', key: 'merchantName' },
        { label: 'Email Address', key: 'merchantEmail' },
        { label: 'Prepaid Wallet Balance', key: 'walletBalance' },
        { label: 'Credit Limit', key: 'creditLimit' },
        { label: 'Due Amount (Postpaid Merchants)', key: 'dueAmount' },
        { label: 'Type', key: 'walletType' },
        { label: 'Status', key: 'isActive' },
    ];

    // Function to handle CSV export
    const handleExportCSV = () => {
        setIsModalOpen(true);
    };

    // Function to confirm download
    const confirmDownload = () => {
        // Round off specific fields and handle NaN
        const processedData = visibleRows.map(item => ({
            ...item,
            walletBalance: Math.round(isNaN(item.walletBalance) ? 0 : item.walletBalance),
            dueAmount: Math.round(isNaN(item.dueAmount) ? 0 : item.dueAmount)
        }));
        const csvData = convertToCSV(processedData, csvHeaders);
        const filename = 'merchant_wallet_data.csv';
        downloadCSV(csvData, filename);
        setIsModalOpen(false);
    };

    return (
        <>
            {(loading || isSubmitting) && <Loader />}
            <PageTitle>Merchant Wallet Balance</PageTitle>

            <TabContext value={tab}>
                <Box sx={{ backgroundColor: '#fff', boxShadow: 1, mb: 2 }}>
                    <TabList onChange={(_e, v) => setTab(v)} indicatorColor="primary" textColor="primary" variant="scrollable">
                        <Tab label="Balances" value="1" />
                        <Tab label="Stranded Residuals" value="2" />
                        <Tab label="Switch History" value="3" />
                    </TabList>
                </Box>

                <TabPanel value="1" sx={{ px: 0, py: 0 }}>
            <Card>
                <CardContent>
                    <FormProvider methods={methods} onSubmit={methods.handleSubmit(onSubmit)}>
                        <Grid container spacing={1} sx={{ display: 'flex', justifyContent: 'left' }}>
                            <Grid item xs={12} md={5}>
                                <RHFSearchableSelectField
                                    width="100%"
                                    name="merchant"
                                    label={`Select Merchant`}
                                    options={_.orderBy(merchantOptions, "label")}
                                />
                            </Grid>
                            <Grid item xs={12} md={3}>
                                <RHFSearchableSelectField
                                    width="100%"
                                    name="walletType"
                                    label={`Select Wallet Type`}
                                    options={WALLET_TYPE_OPTIONS}
                                />
                            </Grid>
                            <Grid item xs={12} md={4}>
                                <Button
                                    type="submit"
                                    variant="contained"
                                    color="primary"
                                    size="small"
                                    sx={{ mt: 2 }}
                                >
                                    Apply Filters
                                </Button>
                                <Button
                                    variant="contained"
                                    color="error"
                                    size="small"
                                    sx={{ mt: 2, ml: 1 }}
                                    onClick={() => {
                                        setFilter(null);
                                        setWalletTypeFilter('');
                                        reset({
                                            merchant: '',
                                            walletType: ''
                                        });
                                    }}
                                >
                                    Reset
                                </Button>
                                <Button
                                    variant="contained"
                                    color="secondary"
                                    size="small"
                                    sx={{ mt: 2, ml: 1 }}
                                    onClick={handleExportCSV}
                                >
                                    Export CSV
                                </Button>
                                <FormControlLabel
                                    sx={{ mt: 2, ml: 1 }}
                                    control={<Switch size="small" checked={includeInactive}
                                        onChange={(e) => setIncludeInactive(e.target.checked)} />}
                                    label={<Typography variant="caption">Include inactive wallets</Typography>}
                                />
                            </Grid>
                        </Grid>
                    </FormProvider>
                </CardContent>
            </Card>




            <Card sx={{ mt: 2 }}>
                <CardContent sx={{ p: 0 }}>
                    <ProzoDataGrid
                        autoHeight={true}
                        columns={columns}
                        getRowId={(row) => `${row.merchantRef}_${row.walletType}`}
                        rows={visibleRows}
                        hideFooterPagination={false}
                        sizePerPage={sizePerPage}
                        setSizePerPage={setSizePerPage}
                        setPage={setPage}
                        page={page}
                        pagination={true}
                        paginationMode='client'
                        rowCount={visibleRows.length}
                    />
                </CardContent>
            </Card>
            <DownloadConfirmationModal
                open={isModalOpen}
                onClose={() => setIsModalOpen(false)}
                onConfirm={confirmDownload}
                reportName="Merchant Wallet Balance"
            />
                </TabPanel>

                <TabPanel value="2" sx={{ px: 0, py: 0 }}>
                    <StrandedResiduals />
                </TabPanel>

                <TabPanel value="3" sx={{ px: 0, py: 0 }}>
                    <WalletSwitchHistory />
                </TabPanel>
            </TabContext>
        </>
    );
}
